import { useSelector, useDispatch } from "react-redux";
import { useMemo } from "react";

import type {
  QuestionMeta,
  UseQuestionHookReturn,
} from "../QuestionProvider/questionHookTypes";
import {
  multiSelectActions,
  selectMultiSelectState,
  type MultiSelectSliceState,
} from "./MultiSelectSlice";

export interface MultiSelectState {
  selectedOptionIds: number[];
  submittedOptionIds: number[];
  attemptsCount: number;
  hasAttempted: boolean;
}

export interface MultiSelectActions {
  toggleOption: (optionId: number) => void;
  submit: () => void;
  reset: () => void;
  tryAgain: () => void;
}

export const useMultiSelect = (
  id: string | number | undefined
): UseQuestionHookReturn<MultiSelectState, MultiSelectActions> => {
  const dispatch = useDispatch();
  const stringId = String(id ?? "default");

  const rawState = useSelector((s: { mscq: MultiSelectSliceState }) =>
    selectMultiSelectState(s, stringId)
  );

  const state: MultiSelectState = useMemo(
    () => ({
      selectedOptionIds: rawState.selected,
      submittedOptionIds: rawState.disabled,
      attemptsCount: rawState.attemptsCount,
      hasAttempted: rawState.hasAttempted,
    }),
    [
      rawState.selected,
      rawState.disabled,
      rawState.attemptsCount,
      rawState.hasAttempted,
    ]
  );

  const actions = useMemo<MultiSelectActions>(
    () => ({
      toggleOption: (optionId: number) =>
        dispatch(
          multiSelectActions.selectOption({
            componentID: stringId,
            optionID: optionId,
          })
        ),
      submit: () => dispatch(multiSelectActions.submit(stringId)),
      reset: () => dispatch(multiSelectActions.reset(stringId)),
      tryAgain: () => dispatch(multiSelectActions.enableAll(stringId)),
    }),
    [dispatch, stringId]
  );

  const meta: QuestionMeta = useMemo(() => ({ id: stringId }), [stringId]);

  return [state, actions, meta];
};
